
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Papa from "papaparse";
import { saveAs } from "file-saver";

// Types for content and related entities
interface ContentItemExport {
  Title: string;
  URL: string;
  Description?: string;
  Notes?: string;
  ContentType: string;
  Collection?: string;
  Tags?: string;
  Boards?: string;
  Thumbnail?: string; // Base64 encoded for manual thumbnails
  Archived: string; // 'Yes' or 'No'
  CreateDate: string;
}

// Function to convert a date to a human-readable format
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

// Function to escape and format multi-value fields (tags, boards)
const formatMultiValues = (values: string[]): string => {
  if (!values || values.length === 0) return '';
  return values.join(', ');
};

// Function to normalize content type
const normalizeContentType = (type: string): string => {
  const validTypes = ["webpage", "image", "video", "article", "document", "audio", "other"];
  const normalized = type.toLowerCase().trim();
  return validTypes.includes(normalized) ? normalized : "webpage";
};

// Function to convert thumbnail URL to base64 if it's a manual thumbnail
const convertThumbnailToBase64 = async (url: string | null): Promise<string | null> => {
  if (!url) return null;
  
  // If it's already a data URL, return as is
  if (url.startsWith('data:')) return url;
  
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting thumbnail to base64:", error);
    return null;
  }
};

// Export content items to CSV
export const exportToCSV = async () => {
  try {
    console.log("Starting CSV export process...");
    toast.info("Preparing CSV export...");
    
    // Fetch all content items for the current user
    const { data: contentItems, error: contentError } = await supabase
      .from('content_items')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (contentError) {
      throw contentError;
    }
    
    if (!contentItems || contentItems.length === 0) {
      toast.info("No content items to export");
      return;
    }
    
    console.log(`Found ${contentItems.length} content items to export`);
    
    // Fetch all collections
    const { data: collections, error: collectionsError } = await supabase
      .from('collections')
      .select('id, name');
    
    if (collectionsError) {
      throw collectionsError;
    }
    
    // Create a map of collection IDs to names
    const collectionMap = new Map();
    collections?.forEach(collection => {
      collectionMap.set(collection.id, collection.name);
    });
    
    // Fetch all tags
    const { data: tags, error: tagsError } = await supabase
      .from('tags')
      .select('id, name');
    
    if (tagsError) {
      throw tagsError;
    }
    
    // Create a map of tag IDs to names
    const tagMap = new Map();
    tags?.forEach(tag => {
      tagMap.set(tag.id, tag.name);
    });
    
    // Fetch all boards
    const { data: boards, error: boardsError } = await supabase
      .from('boards')
      .select('id, title');
    
    if (boardsError) {
      throw boardsError;
    }
    
    // Create a map of board IDs to titles
    const boardMap = new Map();
    boards?.forEach(board => {
      boardMap.set(board.id, board.title);
    });
    
    // Fetch content-collection relationships
    const { data: contentCollections, error: ccError } = await supabase
      .from('content_collections')
      .select('*');
    
    if (ccError) {
      throw ccError;
    }
    
    // Create a map of content IDs to collection IDs
    const contentCollectionMap = new Map();
    contentCollections?.forEach(relation => {
      contentCollectionMap.set(relation.content_id, relation.collection_id);
    });
    
    // Fetch content-tag relationships
    const { data: contentTags, error: ctError } = await supabase
      .from('content_tags')
      .select('*');
    
    if (ctError) {
      throw ctError;
    }
    
    // Create a map of content IDs to arrays of tag IDs
    const contentTagMap = new Map();
    contentTags?.forEach(relation => {
      if (!contentTagMap.has(relation.content_id)) {
        contentTagMap.set(relation.content_id, []);
      }
      contentTagMap.get(relation.content_id).push(relation.tag_id);
    });
    
    // Fetch content-board relationships
    const { data: contentBoards, error: cbError } = await supabase
      .from('content_boards')
      .select('*');
    
    if (cbError) {
      throw cbError;
    }
    
    // Create a map of content IDs to arrays of board IDs
    const contentBoardMap = new Map();
    contentBoards?.forEach(relation => {
      if (!contentBoardMap.has(relation.content_id)) {
        contentBoardMap.set(relation.content_id, []);
      }
      contentBoardMap.get(relation.content_id).push(relation.board_id);
    });
    
    // Process thumbnails to convert to base64 for manual thumbnails
    const csvData: ContentItemExport[] = await Promise.all(contentItems.map(async (item) => {
      // Get collection name
      const collectionId = contentCollectionMap.get(item.id);
      const collectionName = collectionId ? collectionMap.get(collectionId) : '';
      
      // Get tag names
      const tagIds = contentTagMap.get(item.id) || [];
      const tagNames = tagIds.map(id => tagMap.get(id)).filter(Boolean);
      
      // Get board names
      const boardIds = contentBoardMap.get(item.id) || [];
      const boardNames = boardIds.map(id => boardMap.get(id)).filter(Boolean);
      
      // Convert thumbnail to base64 if it exists and is a manual thumbnail
      let thumbnailBase64 = null;
      if (item.thumbnail_url) {
        // Fix for the TypeScript error - safely check metadata
        const metadata = item.metadata as Record<string, any> || {};
        const thumbnailSource = metadata.thumbnail_source || '';
        
        if (thumbnailSource === 'manual') {
          thumbnailBase64 = await convertThumbnailToBase64(item.thumbnail_url);
        } else {
          thumbnailBase64 = item.thumbnail_url;
        }
      }
      
      return {
        Title: item.title || '',
        URL: item.url || '',
        Description: item.description || '',
        Notes: item.notes || '',
        ContentType: item.content_type || 'webpage',
        Collection: collectionName || '',
        Tags: formatMultiValues(tagNames),
        Boards: formatMultiValues(boardNames),
        Thumbnail: thumbnailBase64 || '',
        Archived: item.archived ? 'Yes' : 'No',
        CreateDate: formatDate(item.created_at)
      };
    }));
    
    // Convert to CSV
    const csv = Papa.unparse(csvData, {
      header: true,
      skipEmptyLines: true
    });
    
    // Download the CSV file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `clustr_content_export_${new Date().toISOString().split('T')[0]}.csv`);
    
    toast.success('Export completed successfully');
    console.log('CSV export complete');
  } catch (error) {
    console.error('Export error:', error);
    toast.error('Failed to export data');
  }
};

// Import content from CSV
export const importFromCSV = async (file: File): Promise<void> => {
  try {
    console.log("Starting CSV import process...");
    toast.info("Processing CSV import...");
    
    // Parse the CSV file
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const data = results.data as ContentItemExport[];
            console.log(`Parsed ${data.length} rows from CSV`);
            
            if (data.length === 0) {
              toast.error("No valid data found in CSV");
              reject(new Error("No valid data found in CSV"));
              return;
            }
            
            // Get current user
            const { data: { user } } = await supabase.auth.getUser();
            
            if (!user) {
              toast.error("You must be logged in to import data");
              reject(new Error("User not authenticated"));
              return;
            }
            
            // Fetch existing collections to avoid duplicates
            const { data: existingCollections } = await supabase
              .from('collections')
              .select('name, id')
              .eq('user_id', user.id);
            
            const collectionMap = new Map();
            existingCollections?.forEach(collection => {
              collectionMap.set(collection.name.toLowerCase(), collection.id);
            });
            
            // Fetch existing tags to avoid duplicates
            const { data: existingTags } = await supabase
              .from('tags')
              .select('name, id')
              .eq('user_id', user.id);
            
            const tagMap = new Map();
            existingTags?.forEach(tag => {
              tagMap.set(tag.name.toLowerCase(), tag.id);
            });
            
            // Fetch existing boards to avoid duplicates
            const { data: existingBoards } = await supabase
              .from('boards')
              .select('title, id')
              .eq('user_id', user.id);
            
            const boardMap = new Map();
            existingBoards?.forEach(board => {
              boardMap.set(board.title.toLowerCase(), board.id);
            });
            
            // Process unique collections from the import data
            const uniqueCollections = new Set<string>();
            const uniqueTags = new Set<string>();
            const uniqueBoards = new Set<string>();
            
            data.forEach(item => {
              if (item.Collection) {
                uniqueCollections.add(item.Collection.trim());
              }
              
              if (item.Tags) {
                item.Tags.split(',').forEach(tag => {
                  const trimmedTag = tag.trim();
                  if (trimmedTag) uniqueTags.add(trimmedTag);
                });
              }
              
              if (item.Boards) {
                item.Boards.split(',').forEach(board => {
                  const trimmedBoard = board.trim();
                  if (trimmedBoard) uniqueBoards.add(trimmedBoard);
                });
              }
            });
            
            // Create new collections that don't exist yet
            const newCollections = [...uniqueCollections].filter(name => 
              !collectionMap.has(name.toLowerCase())
            );
            
            console.log(`Creating ${newCollections.length} new collections`);
            
            if (newCollections.length > 0) {
              // Get the highest position for collections
              const { data: positionData } = await supabase
                .from('collections')
                .select('position')
                .order('position', { ascending: false })
                .limit(1)
                .eq('user_id', user.id);
              
              let nextPosition = 1;
              if (positionData && positionData.length > 0) {
                nextPosition = (positionData[0].position || 0) + 1;
              }
              
              // Create collections with incremental positions
              for (const name of newCollections) {
                const { data, error } = await supabase
                  .from('collections')
                  .insert({
                    name,
                    color: '#000000', // Default to black color
                    user_id: user.id,
                    position: nextPosition++
                  })
                  .select('id')
                  .single();
                
                if (error) {
                  console.error(`Error creating collection ${name}:`, error);
                  continue;
                }
                
                collectionMap.set(name.toLowerCase(), data.id);
              }
            }
            
            // Create new tags that don't exist yet
            const newTags = [...uniqueTags].filter(name => 
              !tagMap.has(name.toLowerCase())
            );
            
            console.log(`Creating ${newTags.length} new tags`);
            
            if (newTags.length > 0) {
              for (const name of newTags) {
                const { data, error } = await supabase
                  .from('tags')
                  .insert({
                    name,
                    color: '#000000', // Default to black color
                    user_id: user.id
                  })
                  .select('id')
                  .single();
                
                if (error) {
                  console.error(`Error creating tag ${name}:`, error);
                  continue;
                }
                
                tagMap.set(name.toLowerCase(), data.id);
              }
            }
            
            // Create new boards that don't exist yet
            const newBoards = [...uniqueBoards].filter(title => 
              !boardMap.has(title.toLowerCase())
            );
            
            console.log(`Creating ${newBoards.length} new boards`);
            
            if (newBoards.length > 0) {
              // Get the highest display order for boards
              const { data: orderData } = await supabase
                .from('boards')
                .select('display_order')
                .order('display_order', { ascending: false })
                .limit(1)
                .eq('user_id', user.id);
              
              let nextOrder = 1;
              if (orderData && orderData.length > 0) {
                nextOrder = (orderData[0].display_order || 0) + 1;
              }
              
              // Create boards with incremental display orders
              for (const title of newBoards) {
                const { data, error } = await supabase
                  .from('boards')
                  .insert({
                    title,
                    color: '#000000', // Default to black color
                    user_id: user.id,
                    display_order: nextOrder++
                  })
                  .select('id')
                  .single();
                
                if (error) {
                  console.error(`Error creating board ${title}:`, error);
                  continue;
                }
                
                boardMap.set(title.toLowerCase(), data.id);
              }
            }
            
            // Import the content items
            let importedCount = 0;
            const importErrors: string[] = [];
            
            // Process each row of CSV data
            for (const item of data) {
              try {
                // Skip if URL is missing (required field)
                if (!item.URL) {
                  importErrors.push(`Missing URL for item "${item.Title || 'Untitled'}"`);
                  continue;
                }
                
                // Normalize the content type
                const contentType = normalizeContentType(item.ContentType || 'webpage');
                
                // Determine if the item is archived
                const isArchived = item.Archived?.toLowerCase() === 'yes';
                
                // Create a base metadata object
                let metadata: Record<string, any> = {};
                
                // Handle thumbnail if present
                let thumbnailUrl = null;
                if (item.Thumbnail) {
                  if (item.Thumbnail.startsWith('data:')) {
                    // It's a base64 encoded thumbnail (manual)
                    thumbnailUrl = item.Thumbnail;
                    metadata.thumbnail_source = 'manual';
                    metadata.thumbnail_extraction_status = 'completed';
                  } else {
                    // It's a regular URL
                    thumbnailUrl = item.Thumbnail;
                    metadata.thumbnail_source = 'url';
                    metadata.thumbnail_extraction_status = 'completed';
                  }
                }
                
                // Create the content item
                const { data: contentData, error: contentError } = await supabase
                  .from('content_items')
                  .insert({
                    title: item.Title || 'Untitled',
                    url: item.URL,
                    description: item.Description || null,
                    notes: item.Notes || null,
                    content_type: contentType,
                    archived: isArchived,
                    thumbnail_url: thumbnailUrl,
                    metadata,
                    user_id: user.id
                  })
                  .select('id')
                  .single();
                
                if (contentError) {
                  importErrors.push(`Error creating content for "${item.Title || item.URL}": ${contentError.message}`);
                  console.error('Content creation error:', contentError);
                  continue;
                }
                
                const contentId = contentData.id;
                
                // Associate with collection if provided
                if (item.Collection) {
                  const collectionId = collectionMap.get(item.Collection.toLowerCase());
                  
                  if (collectionId) {
                    const { error: ccError } = await supabase
                      .from('content_collections')
                      .insert({
                        content_id: contentId,
                        collection_id: collectionId
                      });
                    
                    if (ccError) {
                      console.error(`Error associating content with collection ${item.Collection}:`, ccError);
                    }
                  }
                }
                
                // Associate with tags if provided
                if (item.Tags) {
                  const tagNames = item.Tags.split(',').map(t => t.trim()).filter(Boolean);
                  
                  for (const tagName of tagNames) {
                    const tagId = tagMap.get(tagName.toLowerCase());
                    
                    if (tagId) {
                      const { error: ctError } = await supabase
                        .from('content_tags')
                        .insert({
                          content_id: contentId,
                          tag_id: tagId
                        });
                      
                      if (ctError) {
                        console.error(`Error associating content with tag ${tagName}:`, ctError);
                      }
                    }
                  }
                }
                
                // Associate with boards if provided
                if (item.Boards) {
                  const boardNames = item.Boards.split(',').map(b => b.trim()).filter(Boolean);
                  
                  for (const boardName of boardNames) {
                    const boardId = boardMap.get(boardName.toLowerCase());
                    
                    if (boardId) {
                      const { error: cbError } = await supabase
                        .from('content_boards')
                        .insert({
                          content_id: contentId,
                          board_id: boardId,
                          position_x: Math.random() * 800, // Random position
                          position_y: Math.random() * 600  // Random position
                        });
                      
                      if (cbError) {
                        console.error(`Error associating content with board ${boardName}:`, cbError);
                      }
                    }
                  }
                }
                
                importedCount++;
              } catch (itemError: any) {
                console.error('Error processing item:', itemError);
                importErrors.push(`Error processing item "${item.Title || item.URL}": ${itemError.message}`);
              }
            }
            
            console.log(`Successfully imported ${importedCount} content items`);
            
            if (importErrors.length > 0) {
              console.warn(`${importErrors.length} items had errors during import`);
              toast.warning(`Imported ${importedCount} items with ${importErrors.length} errors`);
            } else {
              toast.success(`Successfully imported ${importedCount} content items`);
            }
            
            resolve();
          } catch (error: any) {
            console.error('Import processing error:', error);
            toast.error('Failed to process import data');
            reject(error);
          }
        },
        error: (error: any) => {
          console.error('CSV parsing error:', error);
          toast.error('Failed to parse CSV file');
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Import error:', error);
    toast.error('Failed to import data');
    throw error;
  }
};
